// WS-14 round 4b (+ WS-17 round 4c look): real water (resort.json -> water). Frozen water reads as a smooth, snow-covered ice sheet with
// wind-cleared glossy ice; open water (rapids, ice holes, unfrozen bodies) as dark, reflective water with
// ripples drifting with the current and a shelf-ice rim at its edge. Engine-generic, off when the config is
// absent. No extra geometry or draw calls: the existing ground materials (core terrain, far landscape) are
// patched with a water mask texture (R = water, G = open water, both softened for the shoreline), because
// build_scene.py already levels the terrain to the water surface inside every water body.
// Schema: skill references/landscape.md "Water".
import * as THREE from 'three/webgpu';
import { Fn, If, float, vec2, vec3, vec4, uniform, texture, property, positionWorld, cameraViewMatrix, diffuseColor, fwidth, mix, smoothstep, clamp, max,
  abs, length, normalize, dot, step } from 'three/tsl';
import { inRing } from './lines/measure.js'; // VIS-219: the shared inside test of every area
import { wNoise, wFbm } from './gpu/ground-noise.js';
import { onColor, onRough, onNormal, pname } from './gpu/chain.js';
import { freeOnUpload } from './gpu/release.js';

const setRGB = (rgb) => diffuseColor.assign(vec4(rgb, diffuseColor.a));
/** texture(t) whose .value = another mask of the same size copies that mask into t (main.js redraws the core mask once
 *  the props' ice holes are known): three r186 (WebGPU) kept drawing the first texture after a swap of .value. */
function maskNode(t) {
  const n = texture(t);
  Object.defineProperty(n, 'value', {
    configurable: true, enumerable: true, get() { return this._value; },
    set(v) {
      const o = this._value, a = o?.image, b = v?.image;
      if (o && v && o !== v && a?.data && b?.data && a.width === b.width && a.height === b.height && a.data.length === b.data.length) { a.data.set(b.data); o.needsUpdate = true; } else this._value = v;
    },
  });
  return n;
}

// ---------------------------------------------------------------- masks
function boxBlur(src, w, h, r) { // separable box blur on a Float32Array, r in pixels (3 passes ~ Gaussian)
  if (r < 1) return src;
  // WS-19 (load time): blur only the box around the non-zero pixels (+3r + 2: three passes spread r each), e.g.
  // the open-water channel is a few ice holes in a 1.3 Mpx mask. Outside the box the full blur only carries zeros
  // (plus float residue far below 1/255), so the 8-bit mask texture is identical.
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) { const o = y * w; for (let x = 0; x < w; x++) if (src[o + x] !== 0) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; } }
  if (x1 < 0) return src; // empty mask (e.g. no open water) blurs to itself
  const m = 3 * r + 2; x0 = Math.max(0, x0 - m); y0 = Math.max(0, y0 - m); x1 = Math.min(w - 1, x1 + m); y1 = Math.min(h - 1, y1 + m);
  const sw = x1 - x0 + 1, sh = y1 - y0 + 1;
  if (sw * sh > 0.6 * w * h) return boxBlurFull(src, w, h, r);
  const sub = new Float32Array(sw * sh);
  for (let y = 0; y < sh; y++) sub.set(src.subarray((y0 + y) * w + x0, (y0 + y) * w + x0 + sw), y * sw);
  const bs = boxBlurFull(sub, sw, sh, r), out = new Float32Array(w * h);
  for (let y = 0; y < sh; y++) out.set(bs.subarray(y * sw, y * sw + sw), (y0 + y) * w + x0);
  return out;
}
function boxBlurFull(src, w, h, r) {
  let a = src, b = new Float32Array(a.length);
  for (let pass = 0; pass < 3; pass++) {
    for (let y = 0; y < h; y++) { let acc = 0; const o = y * w;
      for (let x = -r; x <= r; x++) acc += a[o + Math.min(w - 1, Math.max(0, x))];
      for (let x = 0; x < w; x++) { b[o + x] = acc / (2 * r + 1); acc += a[o + Math.min(w - 1, x + r + 1)] - a[o + Math.max(0, x - r)]; } }
    [a, b] = [b, a];
    // WS-19: the vertical pass runs row by row with one accumulator per column (same additions in the same
    // order per column, so the result is bit-identical; it walks memory in order, ~3-4x faster than column by column).
    const acc = new Float64Array(w), k = 2 * r + 1;
    for (let y = -r; y <= r; y++) { const o = Math.min(h - 1, Math.max(0, y)) * w; for (let x = 0; x < w; x++) acc[x] += a[o + x]; }
    for (let y = 0; y < h; y++) {
      const o = y * w, oa = Math.min(h - 1, y + r + 1) * w, os = Math.max(0, y - r) * w;
      for (let x = 0; x < w; x++) { b[o + x] = acc[x] / k; acc[x] += a[oa + x] - a[os + x]; }
    }
    [a, b] = [b, a];
  }
  return a;
}
function toTexture(R, G, w, h) { // WS-77: two channels (RG8): the shaders read .rg only (RGBA8 carried two unused bytes a texel)
  const px = new Uint8Array(w * h * 2);
  for (let i = 0; i < w * h; i++) { px[i * 2] = Math.round(Math.min(1, Math.max(0, R[i])) * 255); px[i * 2 + 1] = Math.round(Math.min(1, Math.max(0, G[i])) * 255); }
  const t = new THREE.DataTexture(px, w, h, THREE.RGFormat);
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearFilter; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.unpackAlignment = 1; // rows of 2 x width bytes (the WebGL2 fallback reads 4-byte aligned rows by default)
  t.needsUpdate = true;
  return freeOnUpload(t); // VIS-267: its pixels (Aurora's core mask 2.5 MB) go once on the graphics chip: nothing reads them back (maskNode's copy is never asked for)
}
const stateAt = (cfg, x, z, bodyOf) => { // 1 = open water, 0 = frozen
  let open = (cfg.state || 'frozen') === 'open' ? 1 : 0;
  for (const b of cfg.bodies || []) if (b.at && bodyOf(b.at[0], b.at[1]) && bodyOf(b.at[0], b.at[1]) === bodyOf(x, z)) open = b.state === 'open' ? 1 : 0;
  for (const o of cfg.openings || []) { // ellipses: { pos, rx, rz (or r), rot }
    const rot = THREE.MathUtils.degToRad(o.rot || 0), dx = x - o.pos[0], dz = z - o.pos[1];
    const u = dx * Math.cos(rot) + dz * Math.sin(rot), v = -dx * Math.sin(rot) + dz * Math.cos(rot);
    if ((u / (o.rx ?? o.r ?? 2)) ** 2 + (v / (o.rz ?? o.r ?? 2)) ** 2 <= 1) open = 1;
  }
  return open;
};

/** Core water mask from scene.json water polygons (1 px per `res` m over the core extent), plus water the
 *  survey land cover knows but the core polygons miss (`extra(x, z)` -> true; e.g. the river in a core corner). */
// WS-19 (load time): scanline fill. For each pixel row it computes the same edge crossings as inRing (lines/measure.js) (same
// formula, same half-open rule) once per row, instead of testing every pixel against every edge: identical pixels,
// far less work for long river polygons. The raster (and the blurred land-cover extra) is cached per water
// array, so the second call after the ice holes are known only redoes the open-water channel.
function rowCrossings(poly, z) {
  const xs = [];
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, zi] = poly[i], [xj, zj] = poly[j];
    if ((zi > z) !== (zj > z)) xs.push(((xj - xi) * (z - zi)) / (zj - zi) + xi);
  }
  return xs.sort((a, b) => a - b);
}
function fillRow(xs, x0, res, i0, i1, out, val) { // out[i] ^= val where an odd number of crossings lie right of the pixel centre
  let k = 0;
  for (let i = i0; i <= i1; i++) { const x = x0 + (i + 0.5) * res; while (k < xs.length && xs[k] <= x) k++; if ((xs.length - k) & 1) out[i] ^= val; }
}
let rasterCache = new WeakMap();
/** VIS-267: the start is over on a visitor's page (main.js): let go of what only the start reads. The raster (Aurora
 *  11.3 MB: inW, the land-cover blur Eb and the shore blur) is a pure cache: a later season with other water (main.js
 *  waterMasksFor, under the season switch's cover) works it out again, and from now on it is not kept (main.js keeps
 *  each season's finished mask, so each is worked out once). */
export function dropRasterCache() { rasterCache = null; }
function waterRaster(extent, water, cfg, res, extra) {
  const key = `${extent.minX},${extent.minZ},${extent.maxX},${extent.maxZ},${res},${cfg.extraSmooth ?? 6}`;
  let per = rasterCache?.get(water) || new Map(); if (rasterCache && !rasterCache.has(water)) rasterCache.set(water, per);
  const hit = per.get(key); if (hit && hit.extra === extra) return hit;
  const w = Math.round((extent.maxX - extent.minX) / res), h = Math.round((extent.maxZ - extent.minZ) / res);
  const inW = new Uint8Array(w * h), row = new Uint8Array(w), hole = new Uint8Array(w), tmp = new Uint8Array(w);
  for (const p of water) {
    const xs = p.outer.map((q) => q[0]), zs = p.outer.map((q) => q[1]), holes = p.holes || [];
    const i0 = Math.max(0, Math.floor((Math.min(...xs) - extent.minX) / res)), i1 = Math.min(w - 1, Math.ceil((Math.max(...xs) - extent.minX) / res));
    const j0 = Math.max(0, Math.floor((Math.min(...zs) - extent.minZ) / res)), j1 = Math.min(h - 1, Math.ceil((Math.max(...zs) - extent.minZ) / res));
    for (let j = j0; j <= j1; j++) {
      const z = extent.minZ + (j + 0.5) * res;
      row.fill(0, i0, i1 + 1); fillRow(rowCrossings(p.outer, z), extent.minX, res, i0, i1, row, 1); // inside the outer ring
      if (holes.length) { // inside ANY hole
        hole.fill(0, i0, i1 + 1);
        for (const hh of holes) { tmp.fill(0, i0, i1 + 1); fillRow(rowCrossings(hh, z), extent.minX, res, i0, i1, tmp, 1); for (let i = i0; i <= i1; i++) hole[i] |= tmp[i]; }
      }
      for (let i = i0; i <= i1; i++) if (row[i] && !(holes.length && hole[i])) inW[j * w + i] = 1;
    }
  }
  let Eb = null;
  if (extra) {
    // WS-17: the survey land cover is a 10 m raster; taken cell by cell its shoreline was a staircase. Blur it
    // over ~a cell and take the 50 % contour: a smooth, rounded shore that stays where the survey puts it.
    const E = new Float32Array(w * h);
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) if (extra(extent.minX + (i + 0.5) * res, extent.minZ + (j + 0.5) * res)) E[j * w + i] = 1;
    Eb = boxBlur(E, w, h, Math.max(1, Math.round((cfg.extraSmooth ?? 6) / res)));
  }
  const r = { w, h, inW, Eb, extra };
  per.set(key, r);
  return r;
}

// WS-42: the global open/frozen state is a uniform (uWOpenAll, applyWaterState), so the masks' G channel only holds the
// LOCAL open water (ice holes, openings, rapids, bodies marked open): the same masks serve every season.
const localOnly = (cfg) => ({ ...cfg, state: 'frozen', far: { ...(cfg.far || {}), state: 'frozen' } });

/** WS-42: water level map (4 m cells, HalfFloat, scene units) for the height gate: water is drawn only where the
 *  ground is at the water surface, so it never climbs a bank (round-7 feedback 05/06). Traced polygons carry their
 *  level (`y`, absolute); water that only the 10 m land cover knows takes the lowest ground within ~8 m (a river's
 *  DEM is flat at its surface; a bank that the coarse raster swallowed is higher and drops out). Levels spread 32 m
 *  outwards so the filtered texture is valid along every shore. groundY(x, z) and Y0 put levels in scene units. */
export function waterLevelMap(extent, water, cfg, extra, groundY, Y0, cell = 4) {
  const w = Math.ceil((extent.maxX - extent.minX) / cell), h = Math.ceil((extent.maxZ - extent.minZ) / cell);
  const lv = new Float32Array(w * h).fill(NaN), lo = new Float32Array(w * h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) { // lowest ground in the cell (5 samples)
    const x = extent.minX + (i + 0.5) * cell, z = extent.minZ + (j + 0.5) * cell, q = cell * 0.35;
    lo[j * w + i] = Math.min(groundY(x, z), groundY(x - q, z - q), groundY(x + q, z - q), groundY(x - q, z + q), groundY(x + q, z + q));
  }
  const sub = [0.25, 0.75];
  for (const p of water) { // traced bodies: their own level, 2 x 2 samples per cell within the body's box
    if (!Number.isFinite(p.y)) continue;
    const xs = p.outer.map((q) => q[0]), zs = p.outer.map((q) => q[1]);
    const i0 = Math.max(0, Math.floor((Math.min(...xs) - extent.minX) / cell)), i1 = Math.min(w - 1, Math.floor((Math.max(...xs) - extent.minX) / cell));
    const j0 = Math.max(0, Math.floor((Math.min(...zs) - extent.minZ) / cell)), j1 = Math.min(h - 1, Math.floor((Math.max(...zs) - extent.minZ) / cell));
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) {
      const hit = sub.some((a) => sub.some((b) => { const pt = [extent.minX + (i + a) * cell, extent.minZ + (j + b) * cell]; return inRing(pt[0], pt[1], p.outer) && !(p.holes || []).some((hh) => inRing(pt[0], pt[1], hh)); }));
      if (hit) { const k = j * w + i; lv[k] = Number.isNaN(lv[k]) ? p.y - Y0 : Math.min(lv[k], p.y - Y0); }
    }
  }
  if (extra) for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) { // survey-only water: the lowest ground nearby
    const k = j * w + i;
    if (!Number.isNaN(lv[k]) || !sub.some((a) => sub.some((b) => extra(extent.minX + (i + a) * cell, extent.minZ + (j + b) * cell)))) continue;
    let m = Infinity; for (let b = -2; b <= 2; b++) for (let a = -2; a <= 2; a++) { const u = i + a, v = j + b; if (u >= 0 && v >= 0 && u < w && v < h) m = Math.min(m, lo[v * w + u]); }
    lv[k] = m;
  }
  for (let it = 0; it < Math.ceil(32 / cell); it++) { // spread outwards (min of known neighbours)
    const nx = lv.slice();
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
      if (!Number.isNaN(lv[j * w + i])) continue;
      let m = Infinity;
      for (let b = -1; b <= 1; b++) for (let a = -1; a <= 1; a++) { const u = i + a, v = j + b; if (u >= 0 && v >= 0 && u < w && v < h && !Number.isNaN(lv[v * w + u])) m = Math.min(m, lv[v * w + u]); }
      if (m < Infinity) nx[j * w + i] = m;
    }
    lv.set(nx);
  }
  // VIS-267 lossless: one half-float channel (R16F), the level the shader reads (.r); RGBA16F carried 6 unread bytes a
  // texel (Aurora 0.63 -> 0.16 MB); filtered the same (each channel on its own)
  const px = new Uint16Array(w * h);
  for (let k = 0; k < w * h; k++) { const v = Number.isNaN(lv[k]) ? -1000 : lv[k]; px[k] = THREE.DataUtils.toHalfFloat(v); }
  const t = new THREE.DataTexture(px, w, h, THREE.RedFormat, THREE.HalfFloatType);
  t.magFilter = THREE.LinearFilter; t.minFilter = THREE.LinearFilter; t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.unpackAlignment = 1; t.needsUpdate = true; // (rows of 2 x width bytes)
  freeOnUpload(t); // VIS-267: only the shaders read it
  return { tex: t, bounds: [extent.minX, extent.minZ, extent.minX + w * cell, extent.minZ + h * cell] };
}

export function coreWaterMask(extent, water, cfg0, res = 1, extra = null) {
  const cfg = localOnly(cfg0); // WS-42: global state = uniform
  const ras = waterRaster(extent, water, cfg, res, extra), { w, h, inW, Eb } = ras;
  const R = new Float32Array(w * h), G = new Float32Array(w * h);
  const bodyOf = (x, z) => water.find((p) => inRing(x, z, p.outer) && !(p.holes || []).some((hh) => inRing(x, z, hh))) || null;
  for (let k = 0; k < w * h; k++) {
    if (!inW[k]) continue;
    const i = k % w, j = (k - i) / w;
    R[k] = 1; G[k] = stateAt(cfg, extent.minX + (i + 0.5) * res, extent.minZ + (j + 0.5) * res, bodyOf);
  }
  if (Eb) {
    const fs = { ...cfg, state: cfg.far?.state || cfg.state, bodies: [] };
    for (let k = 0; k < w * h; k++) {
      if (R[k] || Eb[k] < 0.5) continue;
      const i = k % w, j = (k - i) / w;
      R[k] = 1; G[k] = stateAt(fs, extent.minX + (i + 0.5) * res, extent.minZ + (j + 0.5) * res, () => null);
    }
  }
  // WS-19: R (water / not water) does not depend on the openings, so its blur is cached with the raster.
  const sk = `shore${cfg.shore ?? 1.5}`, Rb = ras[sk] || (ras[sk] = boxBlur(R, w, h, Math.round((cfg.shore ?? 1.5) / res)));
  return { tex: toTexture(Rb, boxBlur(G, w, h, Math.max(1, Math.round(0.6 / res))), w, h), bounds: [extent.minX, extent.minZ, extent.maxX, extent.maxZ] };
}

/** Far-landscape water masks from the land-cover grids (class 3 = water), one per grid. */
export function farWaterMasks(L, cfg0) {
  const cfg = localOnly(cfg0); // WS-42: global state = uniform
  return L.grids.map((g) => {
    const n = g.n, R = new Float32Array(n * n), G = new Float32Array(n * n), far = cfg.far || {};
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
      const k = j * n + i; if (g.cls[k] !== 3) continue;
      R[k] = 1;
      const x = g.minX + i * g.spacing, z = g.minZ + j * g.spacing;
      let open = (far.state || cfg.state || 'frozen') === 'open' ? 1 : 0;
      for (const o of far.open || []) if (Math.hypot(x - o[0], z - o[1]) <= o[2]) open = 1;
      if (far.openRapids) for (const r of L.meta.rapids || []) if (Math.abs(x - r[0]) < far.openRapids && Math.abs(z - r[1]) < far.openRapids && Math.hypot(x - r[0], z - r[1]) <= far.openRapids) { open = 1; break; } // MTK rapids stay open in winter
      G[k] = open;
    }
    return { tex: toTexture(boxBlur(R, n, n, 1), boxBlur(G, n, n, 1), n, n), bounds: [g.minX - g.spacing / 2, g.minZ - g.spacing / 2, g.minX + g.size + g.spacing / 2, g.minZ + g.size + g.spacing / 2] };
  });
}

// ---------------------------------------------------------------- shader (WS-75: node hooks, gpu/chain.js)

/**
 * Patch a ground node material so the water mask shades it as ice or open water.
 * masks: [{ tex, bounds: [minX, minZ, maxX, maxZ] }] (first = finest; later ones are used outside it).
 * opts: { time (a uniform node: seconds), key, cfg }
 * WS-75: colour stage 30 (after the bare-ground layers and the far canopy, before the snow relief), roughness 30,
 * normal 30 (ripples, after the snow relief: open water has no drifts). Returns the uniforms (uniform and texture nodes).
 */
export function patchWater(material, masks, opts) {
  const cfg = opts.cfg || {}, C = cfg.colors || {};
  const flat = new THREE.DataTexture(new Uint16Array([THREE.DataUtils.toHalfFloat(-1000)]), 1, 1, THREE.RedFormat, THREE.HalfFloatType); // (R16F as the level map)
  flat.magFilter = flat.minFilter = THREE.LinearFilter; flat.unpackAlignment = 1; flat.needsUpdate = true; // WS-75: filterable, as a real level map
  const u = {
    // WS-42: whole-water open state (season) and the level gate (water only at its level; crisp shore + wet band)
    uWOpenAll: uniform(0), uWLev: texture(opts.level?.tex || flat), uWLevB: uniform(new THREE.Vector4(...(opts.level?.bounds || [0, 0, 1, 1]))),
    uWGate: uniform(new THREE.Vector4(0, 0.12, 0.3, 0.55)), // (on, tolerance m, wet band height m, wet darkening)
    uWMask0: maskNode(masks[0].tex), uWB0: uniform(new THREE.Vector4(...masks[0].bounds)),
    uWMask1: texture((masks[1] || masks[0]).tex), uWB1: uniform(new THREE.Vector4(...(masks[1] || masks[0]).bounds)),
    uWTime: opts.time || uniform(0),
    uWFlow: uniform(new THREE.Vector2(...(cfg.flow || [0.35, 0.15]))),
    uWIce: uniform(new THREE.Color(C.ice || '#aabccb')), uWSnow: uniform(new THREE.Color(C.snowOnIce || '#e8eef5')), uWBank: uniform(new THREE.Color(C.bank || '#c6d3e0')),
    uWDeep: uniform(new THREE.Color(C.open || '#0b1820')), uWShelf: uniform(new THREE.Color(C.shelf || '#dfe8f1')), uWThin: uniform(new THREE.Color(C.thinIce || '#7a8d9a')), // WS-17: thin new ice round open water
    uWIceShare: uniform(cfg.iceShare ?? 0.15),
    uWRipple: uniform(cfg.ripples ?? 1), // WS-35: ripple strength (calm summer water < 1)
  };
  // what the colour stage leaves for the roughness and the ripples
  const P = { wet: property('float', pname('wWet')), ice: property('float', pname('wIce')), thin: property('float', pname('wThin')), open: property('float', pname('wOpen')), water: property('float', pname('wWater')) };
  const maskAt = (wp) => {
    const q0 = wp.sub(u.uWB0.xy).div(max(u.uWB0.zw.sub(u.uWB0.xy), vec2(1.0))).toVar();
    const m0 = u.uWMask0.sample(clamp(q0, 0.0, 1.0)).rg;
    if (!masks[1]) return m0; // WS-77: one mask (the core): the second tap read the same texel of the same mask
    const q1 = clamp(wp.sub(u.uWB1.xy).div(max(u.uWB1.zw.sub(u.uWB1.xy), vec2(1.0))), 0.0, 1.0);
    const m1 = u.uWMask1.sample(q1).rg;
    const in0 = step(0.0, q0.x).mul(step(0.0, q0.y)).mul(step(q0.x, 1.0)).mul(step(q0.y, 1.0));
    return mix(m1, m0, in0);
  };
  onColor(material, () => {
    const wp = positionWorld.xz.toVar();
    const wm = maskAt(wp).toVar();
    const wPx = length(fwidth(wp)).toVar(); // metres per pixel (outside any branch: derivatives need uniform control flow)
    const wWater = smoothstep(0.35, 0.75, wm.r).toVar(); // soft shoreline (mask is blurred over a few metres)
    // WS-42 level gate (uWGate.x = on): the mask only says where water MAY be; water is drawn where the ground is at
    // the water level (level map), so the shoreline is the terrain's own contour at that level: crisp, never up a bank.
    // Above it a narrow wet band (uWGate.z m of height) darkens the soil. Off (winter) = the soft WS-14 shoreline.
    const G = u.uWGate;
    const wAA = max(fwidth(positionWorld.y), 0.004).toVar(), wRA = max(fwidth(wm.r), 0.015).toVar();
    const wWet = float(0.0).toVar();
    // WS-77: the level map, its noise and the gate are read only while the gate is on (a uniform: the branch is uniform;
    // off, the gate's weight in wOpen below is exactly 1 and nothing else reads it)
    const wGateK = float(1.0).toVar();
    If(G.x.greaterThan(0.5), () => {
      const wlq = clamp(wp.sub(u.uWLevB.xy).div(max(u.uWLevB.zw.sub(u.uWLevB.xy), vec2(1.0))), 0.0, 1.0);
      const wDy = positionWorld.y.sub(u.uWLev.sample(wlq).level(0).r).add(wNoise(wp.mul(0.7)).sub(0.5).mul(0.07)).toVar(); // a little relief: no straight chords along the 3 m mesh (the level map has no mips: level 0 = what it always read)
      const wGate = smoothstep(G.y.sub(wAA), G.y.add(wAA), wDy).oneMinus().toVar();
      wGateK.assign(wGate);
      // water = inside the traced outline (the mask's 50 % line, antialiased, not a blur) AND at the water level
      wWater.assign(smoothstep(wRA.negate().add(0.5), wRA.add(0.5), wm.r).mul(wGate));
      wWet.assign(wWater.oneMinus().mul(smoothstep(0.12, 0.5, wm.r)).mul(smoothstep(G.y, G.y.add(G.z), wDy).oneMinus()));
    });
    // WS-17: open water is the core of the open mask; round it, from the open water outwards: a thin ring of grey
    // new ice, then a rim of wet, trodden slush and snow (client photo Barrel Sauna 19), then the snow-covered ice.
    // WS-42: + the whole water when the season opens it (uWOpenAll, a uniform: the masks hold only local openings)
    const wOpen = max(smoothstep(0.6, 0.78, wm.g).mul(smoothstep(0.2, 0.5, wm.r)).mul(wGateK), u.uWOpenAll.mul(wWater)).toVar();
    const wThin = smoothstep(0.3, 0.52, wm.g).mul(wOpen.oneMinus()).mul(wWater).mul(u.uWOpenAll.oneMinus()).toVar();
    const wRim = smoothstep(0.06, 0.26, wm.g).mul(smoothstep(0.3, 0.5, wm.g).oneMinus()).mul(wWater).mul(u.uWOpenAll.oneMinus()).toVar();
    P.water.assign(wWater); P.wet.assign(wWet); P.thin.assign(wThin); P.open.assign(wOpen); P.ice.assign(0.0);
    setRGB(diffuseColor.rgb.mul(wWet.mul(G.w).oneMinus())); // WS-42: wet soil just above the waterline
    If(wm.r.greaterThan(0.01), () => {
      // WS-17 round 4c: frozen water is a smooth, bright snow sheet on the ice (winter drone photos: the river is a
      // clean white ribbon), with only low-contrast detail: long soft wind drifts along the flow, fine sastrugi that
      // fade out before they alias, a few irregular wind-polished blue-grey patches with soft edges, pressure cracks
      // in the shore band, a blue shade at the bank. No small high-contrast marks (they tiled into "speckles").
      const fdir = normalize(u.uWFlow.add(vec2(1e-4))).toVar();
      const sp = vec2(dot(wp, fdir), dot(wp, vec2(fdir.y.negate(), fdir.x))).toVar();
      const wq = wp.mul(0.016).add(vec2(wFbm(wp.mul(0.009)), wFbm(wp.mul(0.009).add(3.1))).mul(1.8)); // domain warp: irregular patches
      const thr = mix(0.72, 0.42, clamp(u.uWIceShare, 0.0, 1.0)).toVar();
      const ice = smoothstep(thr.sub(0.03), thr.add(0.1), wFbm(wq)).mul(wWater).mul(wOpen.oneMinus()).mul(wThin.oneMinus()).mul(wRim.oneMinus()).toVar();
      P.ice.assign(ice);
      const wBank = smoothstep(0.15, 0.45, wm.r).mul(smoothstep(0.55, 0.85, wm.r).oneMinus()).mul(step(0.5, G.x).oneMinus()); // the shoreline: blue shade of the bank snow (WS-42: none with the gate; the wet band replaces it)
      // WS-77: the snow-on-ice colour only where its weight is not 0 (not on open water, nor in a season that opens the
      // water: uWOpenAll), its fine sastrugi from 2 m per pixel and the pressure cracks from 0.45 m (both weigh 0 there)
      const fw = wWater.mul(wOpen.oneMinus()).mul(u.uWOpenAll.oneMinus()).toVar();
      const c0 = diffuseColor.rgb.toVar();
      If(fw.greaterThan(0.0), () => {
        const drift = wFbm(sp.mul(vec2(0.012, 0.05))).sub(0.5);
        const sastr = float(0.0).toVar();
        If(wPx.lessThan(2.0), () => { sastr.assign(wFbm(sp.mul(vec2(0.06, 0.28)).add(7.0)).sub(0.5).mul(smoothstep(0.6, 2.0, wPx).oneMinus())); });
        const shore = smoothstep(0.45, 0.7, wm.r).mul(smoothstep(0.9, 0.99, wm.r).oneMinus()).toVar();
        const crack = float(0.0).toVar();
        If(wPx.lessThan(0.45).and(shore.greaterThan(0.0)), () => {
          const ridge = abs(wFbm(wp.mul(0.07).add(11.0)).mul(2.0).sub(1.0)).oneMinus();
          crack.assign(smoothstep(0.94, 0.99, ridge).mul(shore).mul(smoothstep(0.15, 0.45, wPx).oneMinus()).mul(wOpen.oneMinus()));
        });
        const f0 = u.uWSnow.mul(drift.mul(0.06).add(1.0).add(sastr.mul(0.035)));
        const frozen = mix(mix(f0, u.uWIce, ice.mul(0.8)), u.uWIce.mul(0.75), crack.mul(0.45));
        c0.assign(mix(c0, frozen, fw)); // WS-42: no frozen fringe round open water
      });
      let c = c0;
      c = mix(c, u.uWBank, wBank.mul(0.45).mul(wOpen.oneMinus()));
      c = mix(c, u.uWShelf, wRim.mul(0.9));
      c = mix(c, u.uWThin, wThin);
      setRGB(mix(c, u.uWDeep, wOpen));
    });
  }, 30);
  // damp, not a mirror: at 0.42 the wet band read as a pale sky-blue rim from above; polished ice, thin ice, open water
  onRough(material, (r) => mix(mix(mix(mix(r, 0.62, P.wet), 0.3, P.ice), 0.18, P.thin), 0.06, P.open), 30);
  // a normal hook returns a node: its statements (If) live in an Fn, built where the normal is (inside its sub-build)
  onNormal(material, (n) => Fn(() => { // ripples drifting with the current (world-space normal from two noise gradients)
    const nn = vec3(n).toVar();
    const wp = positionWorld.xz.toVar(), wPx = length(fwidth(wp)).toVar();
    If(P.open.greaterThan(0.01).and(wPx.lessThan(1.2)), () => {
      const e = 0.35, dr = wp.mul(0.55).sub(u.uWFlow.mul(u.uWTime).mul(1.2)).toVar();
      const h0 = wFbm(dr), hx = wFbm(dr.add(vec2(e, 0.0))), hz = wFbm(dr.add(vec2(0.0, e)));
      const dr2 = wp.mul(1.7).add(vec2(u.uWFlow.y, u.uWFlow.x.negate()).mul(u.uWTime).mul(0.6)).toVar();
      const g0 = wNoise(dr2), gx = wNoise(dr2.add(vec2(e, 0.0))), gz = wNoise(dr2.add(vec2(0.0, e)));
      const wn = normalize(vec3(hx.sub(h0).mul(-0.9).sub(gx.sub(g0).mul(0.35)).mul(u.uWRipple), 1.0, hz.sub(h0).mul(-0.9).sub(gz.sub(g0).mul(0.35)).mul(u.uWRipple)));
      const vn = normalize(cameraViewMatrix.mul(vec4(wn, 0.0)).xyz);
      nn.assign(normalize(mix(nn, vn, P.open.mul(smoothstep(0.25, 1.2, wPx).oneMinus())))); // WS-35: ripples fade before they alias (a far open river sparkled)
    }).ElseIf(P.open.greaterThan(0.01), () => { nn.assign(normalize(nn)); }); // WS-77: from 1.2 m per pixel the ripples weigh 0 (no noise; the same renormalised normal)
    return nn;
  })(), 30);
  material.userData.water = u; // QA / debugging handle (and main.js: a shared bare-season material takes a season's state)
  u.key = opts.key; // WS-77: applyWaterState(u, cfg, u.key) for another season
  material.userData.waterMask = P.water; // WS-75: where this pixel is water, 0..1 (lazy/renderstudio.js: the class map's water)
  applyWaterState(u, cfg, opts.key);
  return u;
}

/** WS-42: everything season-dependent about the water, as uniforms (a live season switch calls this with the new
 *  season's resort.water): whole-water open / frozen, colours, ice share, ripples, flow and the level gate
 *  (`water.level = { gate: true, tolerance: 0.12, wet: 0.3, wetDark: 0.55 }`; core ground only, it has the level map). */
export function applyWaterState(u, cfg = {}, key = 'core') {
  const C = cfg.colors || {};
  const state = (key !== 'core' ? cfg.far?.state : null) || cfg.state || 'frozen';
  u.uWOpenAll.value = state === 'open' ? 1 : 0;
  u.uWIce.value.set(C.ice || '#aabccb'); u.uWSnow.value.set(C.snowOnIce || '#e8eef5'); u.uWBank.value.set(C.bank || '#c6d3e0');
  u.uWDeep.value.set(C.open || '#0b1820'); u.uWShelf.value.set(C.shelf || '#dfe8f1'); u.uWThin.value.set(C.thinIce || '#7a8d9a');
  u.uWIceShare.value = cfg.iceShare ?? 0.15; u.uWRipple.value = cfg.ripples ?? 1;
  u.uWFlow.value.set(...(cfg.flow || [0.35, 0.15]));
  const g = cfg.level;
  u.uWGate.value.set(key === 'core' && g && g.gate !== false ? 1 : 0, g?.tolerance ?? 0.12, g?.wet ?? 0.3, g?.wetDark ?? 0.55);
}
